import { Button } from '@lifeomic/chroma-react/components/Button';
import { ButtonLink } from '@lifeomic/chroma-react/components/ButtonLink';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Plus } from '@lifeomic/chromicons';
import { Radio, RadioGroup } from '@lifeomic/chroma-react/components/Radio';
import { useActiveAccount } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveAccount';
import { useAllAccounts } from '@lifeomic/phc-web-toolkit/src/hooks/useAllAccounts';
import React from 'react';
import urls from '../../util/urls';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';

const messages = defineMessages({
  header: {
    id: 'accountSelectDialog.header',
    defaultMessage: 'Select an account…',
  },
  manage: {
    id: 'accountSelectDialog.manage',
    defaultMessage: 'Manage',
  },
  cancel: {
    id: 'accountSelectDialog.cancel',
    defaultMessage: 'Cancel',
  },
  change: {
    id: 'accountSelectDialog.change',
    defaultMessage: 'Change',
  },
  loading: {
    id: 'accountSelectDialog.loading',
    defaultMessage: 'Loading…',
  },
  createNew: {
    id: 'accountSelectDialog.createNew',
    defaultMessage: 'Create new account',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    background: theme.palette.common.white,
    borderRadius: theme.pxToRem(10),
    display: 'block',
    outline: 'none',
    paddingBottom: theme.spacing(0.25),
    margin: '10vh auto',
    maxWidth: theme.pxToRem(600),
    width: theme.pxToRem(384),
  },
  createNewAccountButton: {
    paddingLeft: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  manageButton: {
    minHeight: theme.pxToRem(4),
    padding: 0,
  },
}));

export const AccountSwitcher = ({
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  isOpen,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  isLoading,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  onAccountChange,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  onCancelClick,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  onCreateAcountClick,
  // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
  onManageClick,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const handleDismiss = () => {
    return onCancelClick?.();
  };

  const allAccounts = useAllAccounts();
  const activeAccount = useActiveAccount();

  // Radio group is value-based, so we will use the "id" of the selected account
  // to determine which radio option is selected (and use the id of each
  // account in the radio option as the "value")
  const [localSelectedAccountId, setLocalSelectedAccountId] = React.useState(
    activeAccount?.id,
  );

  const handleOptionChange = (e: any) => {
    const foundAccount = allAccounts.find(
      account => account.id === e.target.value,
    );

    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    setLocalSelectedAccountId(foundAccount.id);
  };

  const handleChangeClick = () => {
    const foundAccount = allAccounts.find(
      account => account.id === localSelectedAccountId,
    );
    onAccountChange(foundAccount);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      size={0}
      isOpen={isOpen}
      onDismiss={handleDismiss}
      title={formatMessage(messages.header)}
      className={classes.root}
      data-testid="account-switcher-modal"
      actions={
        <React.Fragment>
          <Button variant="text" onClick={handleDismiss} disabled={isLoading}>
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            variant="contained"
            disabled={isLoading}
            onClick={handleChangeClick}
          >
            {isLoading
              ? formatMessage(messages.loading)
              : formatMessage(messages.change)}
          </Button>
        </React.Fragment>
      }
    >
      <>
        <RadioGroup
          value={localSelectedAccountId}
          onChange={handleOptionChange}
          aria-label={formatMessage(messages.header)}
        >
          {allAccounts &&
            allAccounts.map(account => (
              <Radio
                key={account.id}
                value={account.id}
                label={account.name}
                disabled={isLoading}
              >
                <Button
                  className={classes.manageButton}
                  aria-label={`Manage ${account.name} account`}
                  variant="text"
                  onClick={() => onManageClick(account)}
                  {...getTestProps(`manage-account-button-${account.name}`)}
                >
                  {formatMessage(messages.manage)}
                </Button>
              </Radio>
            ))}
        </RadioGroup>
        <ButtonLink
          className={classes.createNewAccountButton}
          variant="text"
          icon={Plus}
          to={urls.app.createAccount()}
          onClick={onCreateAcountClick}
        >
          {formatMessage(messages.createNew)}
        </ButtonLink>
      </>
    </Modal>
  );
};
